import { FormControlLabel, MenuItem, Radio, Typography } from "@mui/material";
import { addDays } from "date-fns";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFormDiagnosis } from "../../../../actions/forms";
import {
  getCantidadDiasBajaMedica,
  getFechaMasReciente,
} from "../../../../utils/datetimeUtils";
import DarkerDisabledTextField from "../../../commons/formComponents/darkerDisabledTextField";
import FormikActions from "../../../commons/formik/formikActions";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikCheckbox from "../../../commons/formik/formikCheckbox";
import FormikDatePicker from "../../../commons/formik/formikDatePicker";
import FormikDateTimePicker from "../../../commons/formik/formikDateTimePicker";
import FormikForm from "../../../commons/formik/formikForm";
import FormikRadio from "../../../commons/formik/formikRadio";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextFieldSpeech from "../../../commons/formik/formikTextFieldSpeech";
import { StyledCommonForms } from "../../../styled/form";
import DropZone from "../dropZone/dropZone";
import FormSectionDivider from "../formSectionDivider";
import { amValidation, validateRequiredFields } from "../formValidation";
import ProfessionalControl from "../professionalControl/professionalControl";
import EspecialidadesContainer from "./especialidadesContainer";

const Am = ({
  handleClose,
  files,
  setFiles,
  handleSubmit,
  model,
  formValues,
  selectedDocument,
  especialidades,
  setEspecialidades,
  isView,
  disabled,
  idArt,
}) => {
  const dispatch = useDispatch();

  const diagnosis = useSelector((state) => state.forms.diagnosis);
  const fields = model.fields;

  const [attachmentError, setAttachmentError] = useState(false);
  const [openAddEspecialidades, setOpenAddEspecialidades] = useState(false);
  const [professionalError, setProfessionalError] = useState(false);

  const fechaAltaMinDate = model.configuration.fechaAltaMin
    ? fields.fechaAlta.validations && fields.fechaAlta.validations.minDate
      ? getFechaMasReciente(
        fields.fechaAlta.validations.minDate,
        fields.fechaPrimeraCuracion.options,
      )
      : fields.fechaPrimeraCuracion.options
    : fields.fechaAlta.validations && fields.fechaAlta.validations.minDate
      ? fields.fechaAlta.validations.minDate
      : undefined;

  return (
    <FormikForm
      artFields={fields}
      initialValues={formValues}
      validationSchema={() =>
        amValidation(
          fechaAltaMinDate,
          model.configuration,
          fields.diasReinicioLaboral.options,
        )
      }
      validate={(values) => {
        const errors = validateRequiredFields(values, fields)
        if (values.tipoAlta === "Continúa tratamiento" &&
          especialidades.length === 0) {
          errors.tipoAlta = "Especialidad continuar tratamiento";
        }
        if (model.configuration.requiresAttachment && files.length === 0) {
          errors.attachment = "Adjunto requerido";
          setAttachmentError(true);
        }
        return errors;
      }}
      onSubmit={(values) => {
        if (!professionalError) {
          handleSubmit({
            ...values,
            diasReinicioLaboral: getCantidadDiasBajaMedica(
              new Date(values.fechaReinicioLaboral),
              fields.fechaPrimeraCuracion.options,
            ),
          });
        }
      }}>
      {({ values, errors, touched, setFieldValue }) => (
        <StyledCommonForms.FormContainer>

          <StyledCommonForms.FieldContainer>
            <FormSectionDivider title="Alta Médica" />
            {model.configuration.showTypeFormCombo && (
              <StyledCommonForms.RowContainer>
                <FormikSelect
                  name="nombreTipoFormulario"
                  label={`Tipo de Formulario Especifico ${fields.nombreTipoFormulario.required ? "*" : ""
                    }`}
                  value={formValues.nombreTipoFormulario}
                  variant="outlined"
                  sx={StyledCommonForms.input}
                  disabled={disabled}>
                  {fields.tiposFormulario.options.map((f) => {
                    return (
                      <MenuItem key={f.nombre} value={f.nombre}>
                        {f.formulario} - {f.nombre}
                      </MenuItem>
                    );
                  })}
                </FormikSelect>
              </StyledCommonForms.RowContainer>
            )}
            <StyledCommonForms.RowContainer>
              <StyledCommonForms.LeftColumnContainer>
                <FormikDateTimePicker
                  name="fechaAlta"
                  label={`Fecha Hora Alta ${fields.fechaAlta.required ? "*" : ""
                    }`}
                  variant="outlined"
                  sx={{ ...StyledCommonForms.input, marginTop: 3 }}
                  disabled={disabled}
                  minDate={fechaAltaMinDate}
                  maxDate={
                    model.configuration.fechaAltaMax ? new Date() : undefined
                  }
                  autoComplete="off"
                />
              </StyledCommonForms.LeftColumnContainer>
              <StyledCommonForms.RightColumnContainer>
                <FormikDatePicker
                  name="fechaReinicioLaboral"
                  label={`Fecha Reinicio Laboral ${fields.fechaReinicioLaboral.required ? "*" : ""
                    }`}
                  variant="outlined"
                  sx={{ ...StyledCommonForms.input, marginTop: 3 }}
                  disabled={
                    disabled || model.configuration.fechaReinicioLaboralDisabled
                  }
                  autoComplete="off"
                  minDate={addDays(values.fechaAlta, 1)}
                />
              </StyledCommonForms.RightColumnContainer>
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <DarkerDisabledTextField
                label="Días totales de baja médica"
                type="text"
                variant="outlined"
                value={getCantidadDiasBajaMedica(
                  new Date(values.fechaReinicioLaboral),
                  fields.fechaPrimeraCuracion.options,
                )}
                sx={StyledCommonForms.input}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikTextFieldSpeech
                id="observaciones"
                name="observaciones"
                label={`Observaciones / Epicrisis ${fields.observaciones.required ? "*" : ""
                  }`}
                type="text"
                variant="outlined"
                multiline
                minRows={3}
                sx={StyledCommonForms.input}
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikSelect
                name="tipoAlta"
                value={formValues.tipoAlta}
                label={`Motivo de Alta ${fields.tipoAlta.required ? "*" : ""}`}
                variant="outlined"
                sx={StyledCommonForms.input}
                disabled={disabled}
                callBack={(value) => {
                  if (value !== "Continúa tratamiento") {
                    setEspecialidades([]);
                  } else if (especialidades.length === 0) {
                    setOpenAddEspecialidades(true);
                  }
                  if (value) {
                    const tipoAltaSelected = fields.tipoAlta.options.find(
                      (ta) => ta.nombre === value,
                    );
                    if (tipoAltaSelected) {
                      fields.fechaReinicioLaboral.required =
                        tipoAltaSelected.requiereReinicioLaboral;
                    }
                  }
                }}>
                {fields.tipoAlta.options.map((ta) => (
                  <MenuItem value={ta.nombre} key={ta.nombre}>
                    {ta.nombre}
                  </MenuItem>
                ))}
              </FormikSelect>
            </StyledCommonForms.RowContainer>
            {values.tipoAlta === "Continúa tratamiento" && (
              <div>
                <FormSectionDivider title="Especialidades continúa tratamiento" />
                <StyledCommonForms.RowContainer>
                  <EspecialidadesContainer
                    especialidades={especialidades}
                    setEspecialidades={setEspecialidades}
                    disabled={disabled}
                    openAdd={openAddEspecialidades}
                    setOpenAdd={setOpenAddEspecialidades}
                    isView={isView}
                    idArt={idArt}
                  />
                </StyledCommonForms.RowContainer>
              </div>
            )}
            <StyledCommonForms.RowContainer>
              <FormikRadio
                name="incapacidad"
                caseInsensitive
                value={formValues.incapacidad}
                label={`Secuelas incapacitantes ${fields.incapacidad.required ? "*" : ""
                  }`}
                disabled={disabled}
                row>
                {fields.incapacidad.options.map((mh) => (
                  <FormControlLabel
                    value={mh.id}
                    control={<Radio />}
                    label={mh.nombre}
                    labelPlacement="end"
                    key={mh}
                  />
                ))}
              </FormikRadio>
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikCheckbox
                name="sugerenciaRecalificacion"
                label={`Recalificacion Profesional ${fields.sugerenciaRecalificacion.required ? "*" : ""
                  }`}
                labelPlacement="end"
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikCheckbox
                name="actividadHabitual"
                label={`Actividad Habitual ${fields.actividadHabitual.required ? "*" : ""
                  }`}
                labelPlacement="end"
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikCheckbox
                name="prestacionesMantenimiento"
                label={`Prestaciones de Mantenimiento ${fields.prestacionesMantenimiento.required ? "*" : ""
                  }`}
                labelPlacement="end"
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikRadio
                name="marcaFirmado"
                value={formValues.marcaFirmado}
                label="¿El trabajador firmó el documento?"
                row
                disabled={disabled}
                callBack={(value) => {
                  if (value !== "Si") {
                    setFieldValue("firmaConforme", null);
                  }
                }}>
                <FormControlLabel
                  value="Si"
                  control={<Radio />}
                  label="Si"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio />}
                  label="No"
                  labelPlacement="end"
                />
              </FormikRadio>
            </StyledCommonForms.RowContainer>
            {values.marcaFirmado === "Si" && (
              <StyledCommonForms.RowContainer>
                <FormikRadio
                  name="firmaConforme"
                  value={formValues.firmaConforme}
                  label="¿El trabajador firmó en conformidad?"
                  row
                  disabled={disabled}>
                  <FormControlLabel
                    value="C"
                    control={<Radio />}
                    label="Si"
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="D"
                    control={<Radio />}
                    label="No"
                    labelPlacement="end"
                  />
                </FormikRadio>
              </StyledCommonForms.RowContainer>
            )}
            <StyledCommonForms.RowContainer>
              <FormikRadio
                name="evaluacionSecuelas"
                label={`¿El trabajador ameritó estimación de secuelas incapacitantes, de conformidad con lo establecido por la Resolución S.R.T. N° 7 de fecha 28 de enero de 2026 o la que en el futuro la reemplace? ${fields.evaluacionSecuelas.required ? "*" : ""
                  }`}
                row
                disabled={disabled}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Si"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  labelPlacement="end"
                />
              </FormikRadio>
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikRadio
                name="requiereTratamientoPsi"
                label={`¿El trabajador ameritó tratamiento psicológico y/o psiquiátrico, de conformidad con lo establecido por la Resolución S.R.T. N° 8 de fecha 28 de enero de 2026 o la que en el futuro la reemplace? ${fields.requiereTratamientoPsi.required ? "*" : ""
                  }`}
                row
                disabled={disabled}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Si"
                  labelPlacement="end"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  labelPlacement="end"
                />
              </FormikRadio>
            </StyledCommonForms.RowContainer>
            <FormSectionDivider title="Diagnóstico" />
            <StyledCommonForms.RowContainer>
              <FormikAutocomplete
                name="diagnosticoOMS"
                label={`Diagnóstico OMS ${fields.diagnosticoOMS.required ? "*" : ""
                  }`}
                variant="outlined"
                options={diagnosis.data}
                getOptions={() => dispatch(getFormDiagnosis(idArt))}
                sx={StyledCommonForms.input}
                renderOption={(option) => {
                  return `${option.id} - ${option.nombre}`;
                }}
                filterOptions={(option) => option.id + option.nombre}
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <StyledCommonForms.RowContainer>
              <FormikTextFieldSpeech
                id="diagnostico"
                name="diagnostico"
                label={`Diagnóstico Actual ${fields.diagnostico.required ? "*" : ""
                  }`}
                type="text"
                variant="outlined"
                multiline
                minRows={3}
                sx={StyledCommonForms.input}
                disabled={disabled}
              />
            </StyledCommonForms.RowContainer>
            <FormSectionDivider title="Profesional" />
            <ProfessionalControl
              disabled={disabled}
              fields={fields}
              setFieldValue={setFieldValue}
              profesionalPreCarga={formValues.profesionalApellidoNombre}
              error={professionalError}
              setError={setProfessionalError}
              especialidadesProfesional={fields.especialidadProfesional}
              values={values}
              habilitarEspecialidadInforme={true}
              isView={isView}
              idArt={idArt}
            />
            <>
              <Typography>{errors.profesionalApellidoNombre}</Typography>
            </>
            {model.configuration.showAttachments && (
              <div>
                <FormSectionDivider title="Adjuntos" />
                <StyledCommonForms.RowContainer>
                  <DropZone
                    files={files}
                    setFiles={setFiles}
                    configuration={model.configuration}
                    error={attachmentError}
                    setError={setAttachmentError}
                    disabled={disabled}
                    selectedDocument={selectedDocument}
                    accept={model.configuration.availableExtensions}
                  />
                </StyledCommonForms.RowContainer>
              </div>
            )}
          </StyledCommonForms.FieldContainer>
          <StyledCommonForms.ActionsContainer>
            <FormikActions handleCancel={handleClose} disabled={disabled} />
          </StyledCommonForms.ActionsContainer>
        </StyledCommonForms.FormContainer>
      )}
    </FormikForm>
  );
};

export default Am;
